export type PHImageSource = "openverse" | "commons" | "wikipedia";
export type PHImageSourceFilter = "all" | PHImageSource;
export type PHImageSearchCursor = Partial<Record<PHImageSource, number>>;
export type PHImageResult = {
    id: string;
    title: string;
    url: string;
    thumbnail: string;
    source: PHImageSource;
    sourceUrl: string;
    credit: string;
};
export type PHImageSearchPage = {
    images: PHImageResult[];
    next: PHImageSearchCursor;
    unavailable: PHImageSource[];
};

const https = (value: unknown): string => {
    if (typeof value !== "string") return "";
    try { const url = new URL(value); return url.protocol === "https:" ? url.href : ""; } catch { return ""; }
};
const plain = (value: unknown): string => typeof value === "string" ? value
    .replace(/<[^>]*>/g, " ")
    .replace(/&(amp|quot|apos|lt|gt|nbsp);/g, (_, entity: string) => ({ amp: "&", quot: '"', apos: "'", lt: "<", gt: ">", nbsp: " " })[entity] || " ")
    .replace(/&#(x[0-9a-f]+|\d+);/gi, (_, code: string) => {
        const point = code[0].toLowerCase() === "x" ? parseInt(code.slice(1), 16) : Number(code);
        return point > 0 && point <= 0x10ffff ? String.fromCodePoint(point) : "";
    }).replace(/\s+/g, " ").trim() : "";
const credit = (...parts: unknown[]) => parts.map(plain).filter(Boolean).join(" · ");

type ImageApiResponse = {
    error?: { info?: string };
    results?: Record<string, unknown>[];
    page_count?: number;
    query?: { pages?: Record<string, WikiImage> };
    continue?: { gsroffset?: number };
};

async function json(url: string, signal?: AbortSignal): Promise<ImageApiResponse> {
    const controller = new AbortController();
    const cancel = () => controller.abort();
    if (signal?.aborted) controller.abort();
    signal?.addEventListener("abort", cancel, { once: true });
    const timer = setTimeout(cancel, 12000);
    try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Image search returned ${response.status}`);
        const body = await response.json() as ImageApiResponse;
        if (!body || typeof body !== "object") throw new Error("Invalid image search response");
        if (body.error) throw new Error(plain(body.error.info) || "Image search unavailable");
        return body;
    } finally {
        clearTimeout(timer);
        signal?.removeEventListener("abort", cancel);
    }
}

type SourcePage = { images: PHImageResult[]; next?: number };

async function openverse(query: string, page: number, signal?: AbortSignal): Promise<SourcePage> {
    const params = new URLSearchParams({ q: query, page: String(page), page_size: "20", mature: "false", filter_dead: "true", excluded_source: "wikimedia" });
    const data = await json(`https://api.openverse.org/v1/images/?${params}`, signal);
    const images: PHImageResult[] = (Array.isArray(data.results) ? data.results : []).flatMap((image: Record<string, unknown>) => {
        const thumbnail = https(image.thumbnail);
        const sourceUrl = https(image.foreign_landing_url);
        if (!thumbnail || !sourceUrl || image.mature === true) return [];
        return [{ id: `openverse:${plain(image.id)}`, title: plain(image.title) || plain(image.creator), url: thumbnail, thumbnail, source: "openverse" as const, sourceUrl, credit: credit(image.creator, image.license, image.license_version, sourceUrl) }];
    });
    return { images, next: page < Number(data.page_count) ? page + 1 : undefined };
}

type WikiImage = {
    title?: string;
    index?: number;
    pageimage?: string;
    thumbnail?: { source?: string };
    imageinfo?: Array<{ url?: string; thumburl?: string; descriptionurl?: string; mime?: string; extmetadata?: Record<string, { value?: string }> }>;
};

function wikiImage(page: WikiImage, source: PHImageSource, title = page.title): PHImageResult[] {
    const info = page.imageinfo?.[0];
    const thumbnail = https(info?.thumburl || info?.url);
    const sourceUrl = https(info?.descriptionurl);
    if (!info || !thumbnail || !sourceUrl || !info.mime?.startsWith("image/")) return [];
    return [{ id: `commons:${page.title}`, title: plain(title).replace(/^File:/, ""), url: thumbnail, thumbnail, source, sourceUrl, credit: credit(info.extmetadata?.Artist?.value, info.extmetadata?.LicenseShortName?.value, sourceUrl) }];
}

const imageInfo = { prop: "imageinfo", iiprop: "url|mime|extmetadata", iiextmetadatafilter: "Artist|LicenseShortName", iiurlwidth: "960" };

async function commons(query: string, offset: number, signal?: AbortSignal): Promise<SourcePage> {
    const params = new URLSearchParams({ action: "query", format: "json", origin: "*", generator: "search", gsrsearch: query, gsrnamespace: "6", gsrlimit: "24", gsroffset: String(offset), ...imageInfo });
    const data = await json(`https://commons.wikimedia.org/w/api.php?${params}`, signal);
    const pages = Object.values(data.query?.pages || {}) as WikiImage[];
    return { images: pages.sort((a, b) => (a.index ?? 0) - (b.index ?? 0)).flatMap(page => wikiImage(page, "commons")), next: typeof data.continue?.gsroffset === "number" ? data.continue.gsroffset : undefined };
}

async function wikipedia(query: string, offset: number, signal?: AbortSignal): Promise<SourcePage> {
    const language = /[\uac00-\ud7af]/.test(query) ? "ko" : "en";
    const params = new URLSearchParams({ action: "query", format: "json", origin: "*", generator: "search", gsrsearch: query, gsrlimit: "12", gsroffset: String(offset), prop: "pageimages", piprop: "thumbnail|name", pithumbsize: "960", pilicense: "free" });
    const data = await json(`https://${language}.wikipedia.org/w/api.php?${params}`, signal);
    const pages = (Object.values(data.query?.pages || {}) as WikiImage[]).filter(page => page.pageimage && page.thumbnail).sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
    if (!pages.length) return { images: [], next: typeof data.continue?.gsroffset === "number" ? data.continue.gsroffset : undefined };
    // Resolve Commons metadata for the free article images before offering them,
    // so attached images retain the author, license and original file page.
    const metadataParams = new URLSearchParams({ action: "query", format: "json", origin: "*", titles: pages.map(page => `File:${page.pageimage}`).join("|"), ...imageInfo });
    const metadata = await json(`https://commons.wikimedia.org/w/api.php?${metadataParams}`, signal);
    const files = Object.values(metadata.query?.pages || {}) as WikiImage[];
    const images = pages.flatMap(page => {
        const file = files.find(item => item.title?.replace(/_/g, " ") === `File:${page.pageimage}`.replace(/_/g, " "));
        return file ? wikiImage(file, "wikipedia", page.title) : [];
    });
    return { images, next: typeof data.continue?.gsroffset === "number" ? data.continue.gsroffset : undefined };
}

export function deduplicatePHImages(images: readonly PHImageResult[]): PHImageResult[] {
    const keys = new Set<string>();
    return images.filter(image => {
        const key = image.sourceUrl.replace(/_/g, " ").replace(/%20/gi, " ");
        if (keys.has(key)) return false;
        keys.add(key);
        return true;
    });
}

export async function searchPHImages(query: string, source: PHImageSourceFilter = "all", cursor?: PHImageSearchCursor, signal?: AbortSignal): Promise<PHImageSearchPage> {
    if (!query.trim()) return { images: [], next: {}, unavailable: [] };
    const providers = { openverse, commons, wikipedia };
    const selected = (source === "all" ? Object.keys(providers) : [source]) as PHImageSource[];
    const active = selected.filter(name => cursor === undefined || cursor[name] !== undefined);
    const settled = await Promise.allSettled(active.map(name => providers[name](query.trim(), cursor?.[name] ?? (name === "openverse" ? 1 : 0), signal)));
    if (signal?.aborted) throw new DOMException("Image search cancelled", "AbortError");
    const images: PHImageResult[] = [];
    const next: PHImageSearchCursor = {};
    const unavailable: PHImageSource[] = [];
    settled.forEach((result, index) => {
        const name = active[index];
        if (result.status === "fulfilled") {
            images.push(...result.value.images);
            if (result.value.next !== undefined) next[name] = result.value.next;
        } else {
            unavailable.push(name);
            next[name] = cursor?.[name] ?? (name === "openverse" ? 1 : 0);
        }
    });
    if (active.length && unavailable.length === active.length) throw new Error("Image search unavailable");
    // Interleave providers so the initial view contains a useful variety instead
    // of requiring a scroll past one entire source before seeing the next.
    const grouped = active.map(name => images.filter(image => image.source === name));
    const interleaved = Array.from({ length: Math.max(0, ...grouped.map(group => group.length)) }, (_, index) => grouped.flatMap(group => group[index] ? [group[index]] : [])).flat();
    return { images: deduplicatePHImages(interleaved), next, unavailable };
}
